import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import React from 'react';
import { AllianceBrand } from './alliance-app-bar';
import { APP_BAR } from './alliance-tokens';
import { BarButton } from './bar-controls';
import { CURRENT_APP } from './current-app';

/** Sidebar width for Contracting and Leads */
const SIDEBAR_WIDTH = 280;

/** Cream sidebar surface */
const SIDEBAR_COLORS = {
  background: '#fffbf3',
  border: '#00000011',
};

/**
 * The drawer surface follows the app's mode: cream for the light sidebar
 * apps, the app's paper color for dark top-nav apps (ARC, University).
 */
const DRAWER_SURFACE =
  CURRENT_APP.mode === 'dark'
    ? { background: CURRENT_APP.background.paper, border: '#ffffff1a' }
    : {
        background: SIDEBAR_COLORS.background,
        border: SIDEBAR_COLORS.border,
      };

/**
 * The navigation drawer. Sidebar apps get a permanent 280px cream drawer at
 * md+ and the same content in a temporary drawer below md. Top-nav apps only
 * use the temporary drawer (pass permanent={false}).
 *
 * The header repeats the bar: wordmark + app name on desktop, the "A" mark
 * and a close button on phones.
 *
 * @param open - Temporary drawer open state (below md)
 * @param onClose - Closes the temporary drawer
 * @param permanent - Render the permanent drawer at md+ (default: sidebar apps)
 * @param width - Drawer width (default 280)
 * @param homeTo - The header brand links to this route and closes the drawer
 *   (top-nav apps: their Home page)
 * @param children - Drawer body under the header
 */
export function AppNavDrawer(props: {
  open: boolean;
  onClose: () => void;
  permanent?: boolean;
  width?: number;
  homeTo?: string;
  children: React.ReactNode;
}) {
  const width = props.width ?? SIDEBAR_WIDTH;
  const permanent = props.permanent ?? CURRENT_APP.layout === 'sidebar';

  const body = (compact: boolean) => (
    <Box
      sx={{
        backgroundColor: DRAWER_SURFACE.background,
        letterSpacing: 'normal',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRight: `1px solid ${DRAWER_SURFACE.border}`,
        boxSizing: 'border-box',
      }}
    >
      <Box
        sx={{
          height: compact ? APP_BAR.heightMobile : APP_BAR.heightDesktop,
          flex: 'none',
          backgroundColor: CURRENT_APP.bar,
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          p: '0 12px 0 20px',
          borderRight: '1px solid #ffffff1a',
          // Cover the drawer's border so the header meets the bar
          mr: '-1px',
        }}
      >
        <AllianceBrand
          compact={compact}
          showName={!compact}
          homeTo={props.homeTo}
          onClick={compact ? props.onClose : undefined}
        />
        <Box sx={{ flex: 1 }} />
        {compact ? (
          <BarButton
            square
            aria-label="Close navigation"
            onClick={props.onClose}
          >
            <CloseIcon sx={{ fontSize: 20 }} />
          </BarButton>
        ) : null}
      </Box>

      <Box
        sx={{
          minHeight: 0,
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {props.children}
      </Box>
    </Box>
  );

  return (
    <Box
      component="nav"
      aria-label="Main"
      sx={{
        width: permanent ? { md: width } : 0,
        flexShrink: { md: 0 },
      }}
    >
      <Drawer
        variant="temporary"
        open={props.open}
        onClose={props.onClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: permanent ? 'none' : 'block' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width,
            border: 'none',
            boxShadow: '0 16px 40px #00000059',
          },
        }}
      >
        {body(true)}
      </Drawer>

      {permanent ? (
        <Drawer
          variant="permanent"
          open
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              border: 'none',
              width,
            },
          }}
        >
          {body(false)}
        </Drawer>
      ) : null}
    </Box>
  );
}
